import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  Stack, 
  IconButton, 
  Select, 
  MenuItem, 
  FormControl,
  InputLabel,
  OutlinedInput
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

interface CategoryOption {
  id: string;
  name: string;
}

interface VenueCategoriesViewProps {
  venue: {
    id: string;
    primaryType?: string;
    categories?: string[];
    neighborhood?: string;
  };        
  onVenueUpdate?: (updatedVenue: any) => void;
  categoryOptions: any[]; // This will be the baseCategories structure
  neighborhoodOptions: any[];
}

// Add this utility function to flatten the categories
const flattenCategories = (categories: any[]): CategoryOption[] => {
  return categories.reduce((acc: CategoryOption[], category) => {
    // Add the main category
    acc.push({ id: category.id, name: category.name });
    
    // Add all subcategories
    if (category.categoryFilters && category.categoryFilters.length > 0) {
      category.categoryFilters.forEach((filter: any) => {
        acc.push({ id: filter.id, name: filter.name });
      });
    }
    
    return acc;
  }, []);
};

export const VenueCategoriesView: React.FC<VenueCategoriesViewProps> = ({ 
  venue, 
  onVenueUpdate, 
  categoryOptions, 
  neighborhoodOptions 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrimaryType, setEditedPrimaryType] = useState(venue.primaryType || '');
  const [editedCategories, setEditedCategories] = useState<string[]>(venue.categories || []);

  // Flatten the categories using useMemo to avoid unnecessary recalculations
  const flattenedCategories = useMemo(() => 
    flattenCategories(categoryOptions),
    [categoryOptions]
  );

  const handleSave = () => {
    if (onVenueUpdate) {
      onVenueUpdate({
        ...venue,
        primaryType: editedPrimaryType,
        categories: editedCategories
      });
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedPrimaryType(venue.primaryType || '');
    setEditedCategories(venue.categories || []);
    setIsEditing(false);
  };

  const handleCategoryChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setEditedCategories(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  if (!categoryOptions || categoryOptions.length === 0) {
    return (
      <Box>
        {venue.primaryType && (
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {venue.primaryType}
          </Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {venue.categories?.map((category, index) => (
            <Chip
              key={`${category}-${index}`}
              label={category}
              size="small"
              variant="outlined"
            />
          ))}
          {(!venue.categories || venue.categories.length === 0) && (
            <Typography variant="body2" color="text.secondary">
              No categories
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {isEditing ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <FormControl fullWidth size="small" sx={{ mr: 1 }}>
              <InputLabel>Primary Type</InputLabel>
              <Select
                value={editedPrimaryType}
                onChange={(e) => setEditedPrimaryType(e.target.value)}
                label="Primary Type"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {flattenedCategories.map((option) => (
                  <MenuItem key={option.id} value={option.id.toLowerCase()}>
                    {option.name.toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton size="small" onClick={handleSave} color="primary">
              <CheckIcon />
            </IconButton>
            <IconButton size="small" onClick={handleCancel} color="error">
              <CloseIcon />
            </IconButton>
          </Box>
          <FormControl fullWidth size="small">
            <InputLabel>Categories</InputLabel>
            <Select
              multiple
              value={editedCategories}
              onChange={handleCategoryChange}
              onClose={() => {}}
              input={<OutlinedInput label="Categories" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip 
                      key={value} 
                      label={value} 
                      size="small"
                    />
                  ))}
                </Box>
              )}
            >
              {flattenedCategories.map((option) => (
                <MenuItem key={option.id} value={option.id.toLowerCase()}>
                  {option.name.toLowerCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {venue.primaryType && (
              <Typography variant="subtitle1" fontWeight="bold">
                {venue.primaryType}
              </Typography>
            )}
            <IconButton 
              size="small" 
              onClick={() => setIsEditing(true)}
              sx={{ ml: 1 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {venue.categories?.map((category, index) => (
              <Chip
                key={`${category}-${index}`}
                label={category}
                size="small"
                variant="outlined"
              />
            ))}
            {(!venue.categories || venue.categories.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                No categories
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};
